import { getExplorerUrl } from "@/config/env";

// Ported from dotone-swap-v3's src/lib/format.ts (ethers.utils.formatUnits /
// BigNumber -> native bigint + formatUnits from viem where it helps, but
// most of this is plain integer/string math so it ports directly).

export function shortAddress(addr: string, lead = 6, tail = 4): string {
  if (addr.length <= lead + tail) return addr;
  return `${addr.slice(0, lead)}…${addr.slice(-tail)}`;
}

export function toDecimalString(value: bigint, decimals: number): string {
  const negative = value < 0n;
  const abs = negative ? -value : value;
  const s = abs.toString().padStart(decimals + 1, "0");
  const whole = s.slice(0, s.length - decimals) || "0";
  const frac = decimals > 0 ? s.slice(s.length - decimals) : "";
  return (negative ? "-" : "") + whole + (frac ? `.${frac}` : "");
}

export function trimTrailingZeros(s: string): string {
  if (!s.includes(".")) return s;
  return s.replace(/0+$/, "").replace(/\.$/, "");
}

export function formatAmount(value: bigint, decimals: number, fixed = 4): string {
  const num = Number(toDecimalString(value, decimals));
  const rounded = num.toFixed(fixed);
  if (num === 0 || Number(rounded) !== 0) return rounded;

  // Non-zero amount that rounds away to "0.0000…" at the requested
  // precision (e.g. a dust-sized swap leg) — widen the precision, capped at
  // the token's own decimals, until the first significant digit shows up.
  for (let f = fixed + 1; f <= decimals; f++) {
    const attempt = num.toFixed(f);
    if (Number(attempt) !== 0) return attempt;
  }
  return num.toFixed(decimals);
}

const COMPACT_UNITS: [number, string][] = [
  [1e12, "T"],
  [1e9, "B"],
  [1e6, "M"],
  [1e3, "K"],
];

export function formatCompact(value: bigint, decimals: number, maxFractionDigits = 2): string {
  const num = Number(toDecimalString(value, decimals));
  if (num === 0) return "0";
  if (num > 0 && num < 0.01) return "<0.01";
  for (const [threshold, suffix] of COMPACT_UNITS) {
    if (Math.abs(num) >= threshold) {
      return trimTrailingZeros((num / threshold).toFixed(maxFractionDigits)) + suffix;
    }
  }
  return trimTrailingZeros(num.toFixed(maxFractionDigits));
}

export function isAmountOverBalance(value: string, decimals: number, balanceRaw: bigint | null): boolean {
  if (balanceRaw === null) return false;
  const parsed = safeParseUnits(value, decimals);
  if (parsed === null) return false;
  return parsed > balanceRaw;
}

/** parseUnits that never throws — returns null for empty/invalid input
 * instead, since form fields go through this on every keystroke. */
export function safeParseUnits(value: string, decimals: number): bigint | null {
  const trimmed = value.trim();
  if (!trimmed || Number.isNaN(Number(trimmed))) return null;
  let s = trimmed;
  if (s.includes("e") || s.includes("E")) s = Number(s).toFixed(decimals);
  const [wholeRaw, fracRaw = ""] = s.split(".");
  const whole = wholeRaw.replace(/[^0-9]/g, "") || "0";
  const frac = fracRaw.replace(/[^0-9]/g, "").slice(0, decimals).padEnd(decimals, "0");
  try {
    return BigInt(whole + frac);
  } catch {
    return null;
  }
}

export function txUrl(hash: string): string {
  return `${getExplorerUrl()}/tx/${hash}`;
}

export function addressUrl(address: string): string {
  return `${getExplorerUrl()}/address/${address}`;
}

const RELATIVE_TIME_UNITS: [number, string][] = [
  [60, "s"],
  [60, "m"],
  [24, "h"],
  [7, "d"],
  [4.345, "w"],
];

/** "3m ago"-style relative timestamp for an ISO date string. */
export function formatTimeAgo(iso: string): string {
  const diffSeconds = (Date.now() - new Date(iso).getTime()) / 1000;
  if (diffSeconds < 5) return "just now";

  let value = diffSeconds;
  let unit = "s";
  for (const [divisor, nextUnit] of RELATIVE_TIME_UNITS) {
    if (value < divisor) break;
    value /= divisor;
    unit = nextUnit;
  }
  return `${Math.floor(value)}${unit} ago`;
}

export function errorMessage(error: unknown, fallback = "Transaction failed"): string {
  if (error instanceof Error) {
    const msg = error.message;
    if (msg.toLowerCase().includes("user rejected") || msg.toLowerCase().includes("user denied")) {
      return "Transaction rejected";
    }
    if (msg.toLowerCase().includes("insufficient funds")) return "Insufficient funds for this transaction";
    // WalletConnect's relay (relay.walletconnect.org, run by Reown) blocks
    // connections from a handful of sanctioned countries at the WebSocket
    // level — surfaces as this exact message via their internal logger
    // (see ConnectWalletButton.tsx's unhandledrejection safety net, since
    // this rejection sometimes escapes their own connect() promise chain).
    // Not something a token/RPC/app-code fix can work around.
    if (msg.toLowerCase().includes("country is blocked")) {
      return "WalletConnect's connection service isn't available in your region. Try connecting a wallet extension (MetaMask) instead.";
    }
    // viem errors are often multi-line with a "Details:" section — keep just the first line.
    const firstLine = msg.split("\n")[0];
    if (firstLine && firstLine.length < 200) return firstLine;
  }
  return fallback;
}
